import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Panel, StatusBadge, PayText, SOURCE_LABEL } from "@/components/extranet/StatusBadge";
import { http, idr, fmtDate, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";

const TABS = [["", "Semua"], ["confirmed", "Dikonfirmasi"], ["pending", "Menunggu"], ["cancelled", "Dibatalkan"], ["no_show", "Tidak hadir"]];

const Detail = ({ b, onStatus }) => (
  <div className="mt-4 space-y-4 text-sm">
    <div className="flex items-center gap-2"><StatusBadge status={b.status} testid="detail-status" /><PayText status={b.payment_status} /></div>
    {[["Tamu", b.guest.name], ["Email", b.guest.email || "-"], ["Telepon", b.guest.phone || "-"], ["Kamar", `${b.room_name} × ${b.rooms}`],
      ["Rate plan", b.rate_plan_name || "-"], ["Pembayaran", b.payment_type === "pay_at_hotel" ? "Bayar di hotel" : "Prepaid"],
      ["Promosi", (b.promotions || []).map((p) => `${p.label} -${p.discount_percent}%`).join(", ") || "-"],
      ["Perangkat / negara", `${b.device || "-"} / ${b.country || "-"}`],
      ["Check-in", fmtDate(b.check_in)], ["Check-out", fmtDate(b.check_out)], ["Malam", b.nights], ["Tamu", `${b.guests} orang`],
      ["Sumber", SOURCE_LABEL(b.source)], ["Ref. eksternal", b.external_ref || "-"], ["Metode bayar", b.payment_method || "-"],
      ["Permintaan khusus", b.special_request || "-"], ["Total", idr(b.grand_total)],
      ...(b.commission_percent != null ? [[`Komisi BE (${b.commission_percent}%)`, `-${idr(b.commission_amount)}`], ["Net ke hotel", idr(b.net_to_hotel)]] : [])].map(([k, v], i) => (
      <div key={i} className="flex justify-between gap-4 border-b border-slate-100 pb-2"><span className="text-slate-500">{k}</span><span className="text-right font-medium capitalize-first">{v}</span></div>
    ))}
    <div className="grid grid-cols-3 gap-2 pt-2">
      <button data-testid="detail-confirm-button" disabled={b.status === "confirmed"} onClick={() => onStatus("confirmed")} className="rounded-md bg-[#16A34A] py-2 font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-40">Konfirmasi</button>
      <button data-testid="detail-noshow-button" disabled={b.status === "no_show"} onClick={() => onStatus("no_show")} className="rounded-md bg-slate-600 py-2 font-semibold text-white transition-colors hover:bg-slate-700 disabled:opacity-40">No-show</button>
      <button data-testid="detail-cancel-button" disabled={b.status === "cancelled"} onClick={() => onStatus("cancelled")} className="rounded-md bg-[#DC2626] py-2 font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-40">Batalkan</button>
    </div>
  </div>
);

export default function Reservations() {
  const { property } = useOutletContext();
  const t = useT();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);

  const load = () => http.get(`/extranet/properties/${property.id}/reservations`, { params: { status, q } }).then((r) => setRows(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [status, q, property.id]); // eslint-disable-line

  const changeStatus = async (s) => {
    try {
      const { data } = await http.patch(`/extranet/bookings/${sel.code}`, { status: s });
      setSel(data); load(); toast.success("Status reservasi diperbarui & dikirim ke channel");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up flex flex-col gap-5">
      <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("Reservasi")}</h1>
      <Panel>
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1">
            {TABS.map(([v, l]) => (
              <button key={v || "all"} data-testid={`res-filter-${v || "all"}`} onClick={() => setStatus(v)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${status === v ? "bg-[#006CE4] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{t(l)}</button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="res-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Cari kode, nama, email")} className="pl-9" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reservations-table">
            <thead className="bg-[#F8FAFC] text-left text-xs uppercase text-slate-500">
              <tr><th className="px-5 py-2.5">Kode</th><th>Nama tamu</th><th>Check-in</th><th>Check-out</th><th>Kamar</th><th>Sumber</th><th>Status</th><th className="text-right">Harga</th><th className="text-right">Komisi</th><th className="pr-5 text-right">Net</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-400">Tidak ada reservasi</td></tr>}
              {rows.map((b) => (
                <tr key={b.code} data-testid={`res-row-${b.code}`} onClick={() => setSel(b)} className="cursor-pointer transition-colors hover:bg-blue-50/50">
                  <td className="px-5 py-3 font-mono-x text-xs text-[#006CE4]">{b.code}</td>
                  <td className="font-medium">{b.guest.name}</td>
                  <td>{fmtDate(b.check_in, "d MMM yyyy")}</td><td>{fmtDate(b.check_out, "d MMM yyyy")}</td>
                  <td>{b.room_name}</td><td className="capitalize text-slate-600">{SOURCE_LABEL(b.source)}</td>
                  <td><StatusBadge status={b.status} /></td>
                  <td className="text-right font-semibold">{idr(b.grand_total)}</td>
                  <td data-testid={`res-commission-${b.code}`} className="text-right text-sm text-slate-700">{b.commission_percent != null ? `${idr(b.commission_amount)} (${b.commission_percent}%)` : "-"}</td>
                  <td data-testid={`res-net-${b.code}`} className="pr-5 text-right font-semibold">{b.net_to_hotel != null ? idr(b.net_to_hotel) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="extranet overflow-y-auto" data-testid="reservation-detail">
          {sel && <><SheetHeader><SheetTitle className="font-mono-x">{sel.code}</SheetTitle></SheetHeader><Detail b={sel} onStatus={changeStatus} /></>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
